<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment, messageStatus } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  // status da mensagem dona (o chip tambem vive fora de <Message />,
  // entao vem por prop e nao pelo contexto)
  messageStatus: {
    type: String,
    default: null,
  },
});

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  if (!attachment.dataUrl) return null;
  return timeStampAppendedURL(attachment.dataUrl);
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const isAudioLoaded = ref(false);
const hasAudioError = ref(false);
// Mensagem otimista (upload rodando): segura no skeleton, nunca "indisponivel"
const inProgress = computed(() => messageStatus === 'progress');
const showAudioError = computed(() => hasAudioError.value && !inProgress.value);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

// MP3 gravado no proprio CRM (lamejs) vem sem cabecalho de duracao: o
// browser reporta Infinity ate ser forcado a materializar o fim do
// arquivo. O truque padrao: seek pra um tempo gigante dispara
// durationchange com o valor real, e ai voltamos pro inicio.
const fixInfiniteDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;

  const onDurationChange = () => {
    if (!Number.isFinite(el.duration)) return;
    el.removeEventListener('durationchange', onDurationChange);
    el.currentTime = 0;
    currentTime.value = 0;
    duration.value = el.duration;
  };
  el.addEventListener('durationchange', onDurationChange);
  el.currentTime = 1e101;
};

const onLoadedMetadata = () => {
  const reported = audioPlayer.value?.duration;
  if (Number.isFinite(reported)) {
    duration.value = reported;
  } else {
    fixInfiniteDuration();
  }
  isAudioLoaded.value = true;
  hasAudioError.value = false;
};

const onAudioError = () => {
  // Only mark as error if we actually had a URL to load
  if (attachment.dataUrl) {
    hasAudioError.value = true;
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  const reported = audioPlayer.value?.duration;
  duration.value = Number.isFinite(reported) ? reported : 0;
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = playbackSpeed.value;
  }
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || !Number.isFinite(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <!-- Using :src directly on <audio> instead of <source> child.
       Per HTML5 spec, changing src on <audio> triggers the load algorithm,
       while changing <source> src is silently ignored. -->
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    :src="timeStampURL"
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="onAudioError"
  />
  <!-- Loading skeleton -->
  <div
    v-if="!isAudioLoaded && !showAudioError"
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)] animate-pulse"
  >
    <div class="size-8 bg-n-alpha-3 rounded-full" />
    <div class="flex-1 h-1 bg-n-alpha-3 rounded-lg" />
    <div class="w-10 h-4 bg-n-alpha-3 rounded" />
  </div>
  <!-- Error state -->
  <div
    v-else-if="showAudioError"
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <Icon icon="i-lucide-circle-off" class="size-4 text-n-slate-11" />
    <span class="text-xs text-n-slate-11">{{
      $t('COMPONENTS.MEDIA.AUDIO_UNAVAILABLE')
    }}</span>
  </div>
  <!-- Player -->
  <div
    v-else
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
